import { amplifyProxy } from '@/services/directApi'

export default {
  namespaced: true,
  state: {
    tenantsActiveRecords: [],
    tenantsArrearsRecords: [],
    tenantsArrearsLessThan61Days: [],
    tenantsArrearsOver61Days: [],
    tenantsNotChasedRecords: [],
    tenantsHistoricRecords: [],
    totalCount: {
      active: 0,
      arrears: 0,
      notChased: 0,
      historic: 0
    },
    pagination: {
      active: { offset: 0, limit: 10 },
      arrears: { offset: 0, limit: 10 },
      notChased: { offset: 0, limit: 10 },
      historic: { offset: 0, limit: 10 }
    },
    loading: {
      active: false,
      arrears: false,
      notChased: false,
      historic: false,
      download: false
    },
    error: {
      active: null,
      arrears: null,
      notChased: null,
      historic: null,
      download: null
    },
    // Page cache to store previously fetched pages
    pageCache: {
      active: {},
      arrears: {},
      notChased: {},
      historic: {}
    }
  },
  mutations: {
    SET_TENANTS_ACTIVE_RECORDS(state, data) {
      state.tenantsActiveRecords = data
    },
    SET_TENANTS_ARREARS_RECORDS(state, data) {
      state.tenantsArrearsRecords = data
      // Maintain the original filtering logic - data is now an array, not data.Data
      state.tenantsArrearsLessThan61Days = data.filter(tenant => tenant.TenantInDebtDuration <= 61)
      state.tenantsArrearsOver61Days = data.filter(tenant => tenant.TenantInDebtDuration > 61)
    },
    SET_TENANTS_NOT_CHASED_RECORDS(state, data) {
      state.tenantsNotChasedRecords = data
    },
    SET_TENANTS_HISTORIC_RECORDS(state, data) {
      state.tenantsHistoricRecords = data
    },
    SET_LOADING(state, { type, value }) {
      state.loading[type] = value
    },
    SET_ERROR(state, { type, error }) {
      state.error[type] = error
    },
    SET_TOTAL_COUNT(state, { type, count }) {
      state.totalCount[type] = count
    },
    SET_PAGINATION(state, { type, pagination }) {
      state.pagination[type] = pagination
    },
    SET_PAGE_CACHE(state, { type, key, data }) {
      state.pageCache[type][key] = data
    },
    CLEAR_PAGE_CACHE(state, type = null) {
      if (type) {
        state.pageCache[type] = {}
      } else {
        state.pageCache = {
          active: {},
          arrears: {},
          notChased: {},
          historic: {}
        }
      }
    }
  },
  actions: {
    async downloadStoGuide({ commit, rootState }, payload) {
      commit('SET_LOADING', { type: 'download', value: true })
      commit('SET_ERROR', { type: 'download', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/tenants/${payload}/sto`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

            'Accept': 'application/octet-stream'
          },
          response: true
        }

        const response = await amplifyProxy.get(endpoint, options)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const fileURL = window.URL.createObjectURL(blob)
        const fileLink = document.createElement('a')
        
        fileLink.href = fileURL
        fileLink.setAttribute('download', 'file.pdf')
        document.body.appendChild(fileLink)
        fileLink.click()
        document.body.removeChild(fileLink)
        window.URL.revokeObjectURL(fileURL)
        
        return response
      } catch (error) {
        commit('SET_ERROR', { type: 'download', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'download', value: false })
      }
    },

    async getTenantsActiveRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, tenantId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${tenantId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.active[cacheKey]) {
        const cachedData = state.pageCache.active[cacheKey]
        const data = cachedData?.Data || cachedData || []
        const count = cachedData?.Count || data.length
        
        commit('SET_TENANTS_ACTIVE_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'active', count })
        commit('SET_PAGINATION', { type: 'active', pagination: { offset, limit } })
        return { data, count }
      }
      
      commit('SET_LOADING', { type: 'active', value: true })
      commit('SET_ERROR', { type: 'active', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/tenants/active/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(tenantId && { tenantId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'active', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        
        commit('SET_TENANTS_ACTIVE_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'active', count })
        commit('SET_PAGINATION', { type: 'active', pagination: { offset, limit } })
        return { data, count }
      } catch (error) {
        commit('SET_ERROR', { type: 'active', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'active', value: false })
      }
    },

    async getTenantsArrearsRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, tenantId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${tenantId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.arrears[cacheKey]) {
        const cachedData = state.pageCache.arrears[cacheKey]
        const data = cachedData?.Data || cachedData || []
        const count = cachedData?.Count || data.length
        
        commit('SET_TENANTS_ARREARS_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'arrears', count })
        commit('SET_PAGINATION', { type: 'arrears', pagination: { offset, limit } })
        return { data, count }
      }
      
      commit('SET_LOADING', { type: 'arrears', value: true })
      commit('SET_ERROR', { type: 'arrears', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/tenants/arrears/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(tenantId && { tenantId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'arrears', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        
        commit('SET_TENANTS_ARREARS_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'arrears', count })
        commit('SET_PAGINATION', { type: 'arrears', pagination: { offset, limit } })
        return { data, count }
      } catch (error) {
        commit('SET_ERROR', { type: 'arrears', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'arrears', value: false })
      }
    },

    async getTenantsNotChasedRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, tenantId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${tenantId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.notChased[cacheKey]) {
        const cachedData = state.pageCache.notChased[cacheKey]
        const data = cachedData?.Data || cachedData || []
        const count = cachedData?.Count || data.length
        
        commit('SET_TENANTS_NOT_CHASED_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'notChased', count })
        commit('SET_PAGINATION', { type: 'notChased', pagination: { offset, limit } })
        return { data, count }
      }
      
      commit('SET_LOADING', { type: 'notChased', value: true })
      commit('SET_ERROR', { type: 'notChased', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/tenants/notchased/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(tenantId && { tenantId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'notChased', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        
        commit('SET_TENANTS_NOT_CHASED_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'notChased', count })
        commit('SET_PAGINATION', { type: 'notChased', pagination: { offset, limit } })
        return { data, count }
      } catch (error) {
        commit('SET_ERROR', { type: 'notChased', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'notChased', value: false })
      }
    },

    async getTenantsHistoricRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, tenantId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${tenantId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.historic[cacheKey]) {
        const cachedData = state.pageCache.historic[cacheKey]
        const data = cachedData?.Data || cachedData || []
        const count = cachedData?.Count || data.length
        
        commit('SET_TENANTS_HISTORIC_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'historic', count })
        commit('SET_PAGINATION', { type: 'historic', pagination: { offset, limit } })
        return { data, count }
      }
      
      commit('SET_LOADING', { type: 'historic', value: true })
      commit('SET_ERROR', { type: 'historic', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/tenants/historic/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(tenantId && { tenantId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'historic', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        
        commit('SET_TENANTS_HISTORIC_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'historic', count })
        commit('SET_PAGINATION', { type: 'historic', pagination: { offset, limit } })
        return { data, count }
      } catch (error) {
        commit('SET_ERROR', { type: 'historic', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'historic', value: false })
      }
    }
  }
}